import React, { useEffect, useState } from "react";
import { History } from "lucide-react";
import api from "../lib/api";
import { fmtDateTime } from "../lib/time";
import { Label } from "./common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const FIELD_LABELS = { name: "Name", price: "Price", max_locations: "Max locations", max_employees: "Max employees",
  features: "Features", interval: "Billing interval", currency: "Currency", tier: "Tier" };
const ACTION_STYLE = { CREATED: "bg-emerald-50 text-emerald-700", UPDATED: "bg-indigo-50 text-indigo-700", DELETED: "bg-red-50 text-red-700" };

const fmt = (field, v) => {
  if (field === "features") return v?.length ? v.join(", ") : "none";
  if (field === "max_locations" || field === "max_employees") return v == null ? "Unlimited" : String(v);
  if (field === "price") return v == null ? "—" : `R${v}`;
  return v == null || v === "" ? "—" : String(v);
};
const when = (iso) => fmtDateTime(iso);

function ChangeList({ changes, action }) {
  if (action !== "UPDATED") return <span className="text-slate-500">{action === "CREATED" ? "Plan created" : "Plan deleted"}</span>;
  return (
    <ul className="space-y-0.5">
      {changes.map((c) => (
        <li key={c.field} className="text-slate-600">
          <span className="font-semibold text-slate-700">{FIELD_LABELS[c.field] || c.field}:</span>{" "}
          <span className="text-slate-400 line-through">{fmt(c.field, c.from)}</span> → <span className="font-semibold text-slate-900">{fmt(c.field, c.to)}</span>
        </li>
      ))}
    </ul>
  );
}

function HistoryTable({ rows, showPlan, testId }) {
  if (!rows.length) return <p className="py-6 text-center text-sm text-slate-400" data-testid={`${testId}-empty`}>No plan changes recorded yet.</p>;
  return (
    <div className="overflow-x-auto sv-scroll">
      <table className="w-full min-w-[640px] text-sm" data-testid={testId}>
        <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
          <th className="py-2">When</th>{showPlan && <th>Plan</th>}<th>Action</th><th>What changed</th><th>By</th></tr></thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.id} className="border-b border-slate-100 align-top" data-testid={`plan-change-${h.id}`}>
              <td className="whitespace-nowrap py-3 pr-3 text-slate-400">{when(h.created_at)}</td>
              {showPlan && <td className="py-3 pr-3 font-semibold text-slate-800">{h.plan_name}</td>}
              <td className="py-3 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${ACTION_STYLE[h.action]}`}>{h.action.toLowerCase()}</span></td>
              <td className="py-3 pr-3"><ChangeList changes={h.changes} action={h.action} /></td>
              <td className="py-3 text-slate-500">{h.actor_name || h.actor_email}<span className="block text-xs text-slate-400">{h.actor_name ? h.actor_email : ""}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PlanChangeHistory({ plans, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [planId, setPlanId] = useState("all");
  useEffect(() => {
    const q = planId !== "all" ? `?plan_id=${planId}` : "";
    api.get(`/platform/plans/history${q}`).then((r) => setRows(r.data)).catch(() => {});
  }, [planId, refreshKey]);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="plan-change-history">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><History className="h-4 w-4 text-slate-400" /><Label>Plan change history</Label></div>
        <Select value={planId} onValueChange={setPlanId}>
          <SelectTrigger className="w-48" data-testid="plan-history-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <HistoryTable rows={rows} showPlan testId="plan-history-table" />
    </div>
  );
}

export function PlanHistoryDialog({ plan, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!plan) return;
    setRows(null);
    api.get(`/platform/plans/history?plan_id=${plan.id}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [plan]);
  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="plan-history-dialog">
        <DialogHeader><DialogTitle>Change history · {plan?.name}</DialogTitle></DialogHeader>
        {rows == null ? <p className="py-6 text-center text-sm text-slate-400">Loading…</p> : <HistoryTable rows={rows} testId="plan-history-dialog-table" />}
      </DialogContent>
    </Dialog>
  );
}
